import React from "react";
import { MonthCaptionProps, useNavigation } from "react-day-picker";

import { de } from "date-fns/locale";

import { endOfMonth, endOfYear, format, setYear, startOfMonth } from "date-fns";
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
} from "@mui/material";
import ArrowLeft from "@icons/myicons/arrow-left.svg?react";

const DateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "4px",
  marginBottom: theme.spacing(2),

  "& .MuiInputBase-root::before, & .MuiInputBase-root::after": {
    display: "none",
  },
  "& .MuiButtonBase-root": {
    height: "100%",
  },
}));

interface Props extends MonthCaptionProps {
  disableFuture?: boolean;
  disablePast?: boolean;
}

export const DateRangeCaptionLabel = ({
  calendarMonth,
  disableFuture,
  disablePast,
}: Props) => {
  const { goToMonth } = useNavigation();

  const safeMonth = calendarMonth.date ?? new Date();

  const handleYearChange = (e: SelectChangeEvent<number>) => {
    const newMonth = setYear(startOfMonth(safeMonth), Number(e.target.value));
    goToMonth(newMonth);
  };

  const handleMonthChange = (e: SelectChangeEvent<number>) => {
    const monthIndex = Number(e.target.value);
    const newMonth = new Date(safeMonth.getFullYear(), monthIndex, 1);
    goToMonth(newMonth);
  };

  const currentYear = new Date().getFullYear();
  // Calculate year range for the select dropdown
  const minYear = disablePast ? currentYear : 1900;
  const maxYear = disableFuture ? currentYear : currentYear + 50;

  const handlePrevMonth = () => {
    const prev = new Date(safeMonth.getFullYear(), safeMonth.getMonth() - 1, 1);
    goToMonth(prev);
  };

  const handleNextMonth = () => {
    const next = new Date(safeMonth.getFullYear(), safeMonth.getMonth() + 1, 1);

    if (disableFuture && endOfMonth(next) > endOfYear(new Date())) {
      return;
    }

    goToMonth(next);
  };

  const canGoNext = disableFuture
    ? endOfMonth(safeMonth) < endOfYear(new Date())
    : true;

  return (
    <DateContainer
      display="flex"
      alignItems="center"
      className="date-container"
    >
      <IconButton
        size="small"
        onClick={handlePrevMonth}
        sx={{
          width: "36px",
          height: "36px !important",
        }}
      >
        <ArrowLeft width={20} height={20} />
      </IconButton>
      <Box
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        display="flex"
        gap="4px"
      >
        <Select
          value={safeMonth.getMonth()}
          onChange={handleMonthChange}
          MenuProps={{
            PaperProps: { sx: { maxHeight: 200 } },
            sx: { zIndex: 1301 },
          }}
          sx={{
            width: "130px",
          }}
        >
          {Array.from({ length: 12 }, (_, i) => {
            return (
              <MenuItem key={i + 1} value={i}>
                {format(new Date(2025, i), "MMMM", {
                  locale: de,
                })}
              </MenuItem>
            );
          })}
        </Select>
        <Select
          value={safeMonth.getFullYear()}
          onChange={handleYearChange}
          MenuProps={{
            PaperProps: { sx: { maxHeight: 200 } },
            sx: { zIndex: 1301 },
          }}
        >
          {Array.from({ length: maxYear - minYear + 1 }, (_, i) => (
            <MenuItem key={i} value={maxYear - i}>
              {maxYear - i}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <IconButton
        size="small"
        onClick={handleNextMonth}
        disabled={!canGoNext}
        sx={{
          width: "36px",
          height: "36px !important",
        }}
      >
        <ArrowLeft
          width={20}
          height={20}
          style={{ transform: "rotate(180deg)" }}
        />
      </IconButton>
    </DateContainer>
  );
};
